/**
 * Brand-safety key-values for display-ad requests (WEB_ADS_BRAND_SAFETY).
 *
 * Mirrors what the iOS and Android apps attach to every GAM request
 * (FLAdManager.m brandSafetyParametersForAdjacentItems:, AdHelper.kt
 * getCustomTargetingMap) so that line-item exclusions and DoubleVerify's
 * creative-side measurement see the same signals on web as in the apps:
 *
 *   brand_safety_tags         union of the adjacent items' `b:*` tags
 *   adjacent_topics           union of the adjacent items' topic tags
 *   content_urls              the adjacent items' source URLs
 *   turl                      the first adjacent item's source URL (DV reads
 *                             this as the content URL of the impression)
 *   isFirstAdjacentItemClean  "1" when the first adjacent item is tagged
 *                             exactly `b:clean`, "0" when it carries any other
 *                             tag, and ABSENT when it has no tags at all. The
 *                             apps omit the key for untagged items rather than
 *                             sending "0", and DV reporting only lines up
 *                             across platforms if web does the same.
 *
 * The `b:*` tags come from flap on every item as additionalUsage.brandSafety
 * (clean, unknown, adult, hatespeech, ...); `b:unknown` is NOT clean.
 *
 * Pure functions, no GPT or redux access, so the placements can call this in
 * render and AdSlot's targeting diff stays stable for the same items.
 */

export const BRAND_SAFETY_CLEAN_TAG = 'b:clean';

export const BRAND_SAFETY_KEYS = {
  TAGS: 'brand_safety_tags',
  ADJACENT_TOPICS: 'adjacent_topics',
  CONTENT_URLS: 'content_urls',
  TURL: 'turl',
  IS_FIRST_ADJACENT_ITEM_CLEAN: 'isFirstAdjacentItemClean',
} as const;

/** The slice of an item the key-values are derived from. */
export type BrandSafetyItem = {
  sourceURL?: string | null;
  sectionLinks?: Array<{ type?: string; topicTag?: string }> | null;
  additionalUsage?: { brandSafety?: unknown } | null;
};

export type BrandSafetyTargeting = Partial<
  Record<
    typeof BRAND_SAFETY_KEYS[keyof typeof BRAND_SAFETY_KEYS],
    string | Array<string>
  >
>;

const uniqueStrings = (values: Array<unknown>): Array<string> => {
  const seen = new Set<string>();
  const out: Array<string> = [];
  values.forEach((value) => {
    if (typeof value === 'string' && value && !seen.has(value)) {
      seen.add(value);
      out.push(value);
    }
  });
  return out;
};

/** The item's `b:*` tags, or [] when it has none. */
export const brandSafetyTags = (
  item: BrandSafetyItem | null | undefined,
): Array<string> => {
  const tags = item?.additionalUsage?.brandSafety;
  return Array.isArray(tags) ? uniqueStrings(tags) : [];
};

/** Only a literal `b:clean` counts; `b:unknown` and an empty list do not. */
export const isBrandSafetyClean = (tags: Array<string>): boolean =>
  tags.includes(BRAND_SAFETY_CLEAN_TAG);

const topicTags = (item: BrandSafetyItem): Array<string> =>
  (item.sectionLinks || [])
    .filter((link) => link?.type === 'topic')
    .map((link) => link.topicTag)
    .filter((tag): tag is string => typeof tag === 'string');

/**
 * Key-values for a slot whose neighbouring content is `adjacentItems`, in
 * display order (the item directly above the slot first). Null entries are
 * skipped; keys with nothing to say are omitted rather than sent empty.
 */
export const brandSafetyTargeting = (
  adjacentItems: Array<BrandSafetyItem | null | undefined>,
): BrandSafetyTargeting => {
  const items = adjacentItems.filter((item): item is BrandSafetyItem => !!item);
  const targeting: BrandSafetyTargeting = {};
  if (items.length === 0) {
    return targeting;
  }

  const tags = uniqueStrings(items.flatMap(brandSafetyTags));
  if (tags.length > 0) {
    targeting[BRAND_SAFETY_KEYS.TAGS] = tags;
  }
  const topics = uniqueStrings(items.flatMap(topicTags));
  if (topics.length > 0) {
    targeting[BRAND_SAFETY_KEYS.ADJACENT_TOPICS] = topics;
  }
  const urls = uniqueStrings(items.map((item) => item.sourceURL));
  if (urls.length > 0) {
    targeting[BRAND_SAFETY_KEYS.CONTENT_URLS] = urls;
  }

  const first = items[0];
  if (typeof first.sourceURL === 'string' && first.sourceURL) {
    targeting[BRAND_SAFETY_KEYS.TURL] = first.sourceURL;
  }
  const firstTags = brandSafetyTags(first);
  if (firstTags.length > 0) {
    targeting[BRAND_SAFETY_KEYS.IS_FIRST_ADJACENT_ITEM_CLEAN] =
      isBrandSafetyClean(firstTags) ? '1' : '0';
  }
  return targeting;
};
